<template>
  <div id="AccountFormResponses" class="Main-Content">

    <Page
      :title="$t('Form Responses')"
    >
      <LoadingLayout v-if="dataLoading"/>
      <Layout v-else>
        <LayoutSection>
          <Card :actions="[{
            label: $t('Download'),
            onAction: download,
            disabled: !totalElements || exporting
          }]">
            <Filters
              ref="filters"
              :filters="[
                {
                  key: 'fromDate',
                  label: $t('Start Date'),
                  type: 'date',
                  required: true
                },
                {
                  key: 'toDate',
                  label: $t('End Date'),
                  type: 'date'
                },
                {
                  key: 'id',
                  label: $t('ID'),
                  type: 'text'
                },
                {
                  key: 'status',
                  label: $t('Status'),
                  type: 'select',
                  options: [
                    { label: $t('New'), value: 'New' },
                    { label: $t('Archived'), value: 'Archived' }
                  ],
                  required: false
                },
                {
                  key: 'campaignId',
                  label: $t('Campaign'),
                  type: 'select',
                  options: campaignList,
                  multiple: true
                },
                {
                  key: 'widgetId',
                  label: $t('Widget'),
                  type: 'select',
                  options: widgetList,
                  multiple: true
                }
              ]"
              :shortcuts="[
                {
                  label: $t('Type'),
                  items: [
                    { label: $t('New'), onAction: () => setStatus('New') },
                    { label: $t('Archived'), onAction: () => setStatus('Archived') }
                  ]
                }
              ]"
              :applied-filters="filter"
              @update="updateFilter"
              :query="false"
              required
            />
            <DataTable
              :columns="[
                {
                  key: 'createdOn',
                  label: $t('Date'),
                  format: 'dateTimeLong'
                },
                {
                  key: 'campaignName',
                  label: $t('Campaign')
                },
                {
                  key: 'widgetName',
                  label: $t('Widget')
                },
                {
                  key: 'status',
                  label: $t('Status')
                }
              ]"
              :rows="sortedResponses"
              :pagination="{
                offset: offset,
                limit: limit,
                total: totalElements,
                updateOffset: updateOffset,
                updateLimit: updateLimit
              }"
              @click-row="(row) => selectedResponse = row"
              :loading="loadingState"
            />
          </Card>
          <Sheet 
            :title="$t('AccountFormResponses.Form Response Details')"
            :width="900"
            :visible="!!selectedResponse"
            @close="selectedResponse = null"
            padded
          >  
            <template v-if="selectedResponse">
              <Card :title="$t('Summary')">
                <CardSection>
                  <Stack distribution="fill">
                    <Stat
                      :label="$t('Date')"
                      type="dateTimeLong"
                      :value="selectedResponse.createdOn"
                    ></Stat> 
                    <Stat
                      :label="$t('Campaign')"
                      :value="selectedResponse.campaignName"
                    ></Stat>
                    <Stat
                      :label="$t('Widget')"
                      :value="selectedResponse.widgetName"
                    ></Stat>
                    <Stat
                      :label="$t('Status')"
                      :value="selectedResponse.status"
                    ></Stat>
                  </Stack>
                </CardSection>
              </Card>
              <Card v-if="selectedJson" :title="$t('Properties')">
                <CardSection>
                  <Stats>
                    <Stat v-for="(prop, index) in selectedProps" :key="index" :label="prop.name" :value="prop.value"/>
                  </Stats>
                </CardSection>
              </Card>
              <Card v-if="selectedCart && selectedCart.products.length" :title="$t('Cart Details')">
                <DataTable
                  :columns="[
                    {
                      key: 'image',
                      label: $t('Image')
                    },
                    {
                      key: 'name',
                      label: $t('Name')
                    },
                    {
                      key: 'categories',
                      label: $t('Categories')
                    },
                    {
                      key: 'sku',
                      label: $t('SKU')
                    },
                    {
                      key: 'price',
                      label: $t('Price'),
                      format: 'currency',
                      formatContext: row => ({currency: this.selectedCart && this.selectedCart.currency || this.theUser.accountCurrency}),
                    },
                    {
                      key: 'quantity',
                      label: $t('Quantity')
                    }
                  ]"
                  :rows="selectedCart.products"
                >
                  <template v-slot:col:name="{row}">
                    <PageLink v-if="row.url" :url="row.url" external>{{row.name}}</PageLink>
                    <template v-else>{{row.name}}</template>
                  </template>
                  <template v-slot:col:image="{row}">
                    <img v-if="row.image" style="display: block; max-height: 100px; max-width: 50px;" :src="row.image" :alt="row.name"/>
                  </template>
                  <template v-slot:col:categories="{row}">
                    {{getCategories(row.categories)}}
                  </template>
                </DataTable>
                <CardSection>
                  <Stats>
                    <Stat :label="$t('Discount')" :value="selectedCart.discount" :currency="selectedCart.currency || theUser.accountCurrency" type="currency"/>
                    <Stat :label="$t('Discount Code')" :value="selectedCart.discountCode"/>
                    <Stat :label="$t('Total')" :value="cartTotal" :currency="selectedCart.currency || theUser.accountCurrency" type="currency"/>
                    <Stat :label="$t('Tax')" :value="selectedCart.tax" :currency="selectedCart.currency || theUser.accountCurrency" type="currency"/>
                    <Stat :label="$t('Shipping')" :value="selectedCart.shipping" :currency="selectedCart.currency || theUser.accountCurrency" type="currency"/>
                  </Stats>
                </CardSection>
              </Card>
              <Card v-if="selectedResponse.widgetLanguage" :title="$t('Widget Details')">
                <CardSection>
                  <Stats>
                    <Stat :label="$t('Language')" :value="selectedResponse.widgetLanguage"/>
                    <Stat :label="$t('creative.Breakpoint')" :value="selectedResponse.widgetBreakpoint || `'${$t('Default')}'`"/>
                    <Stat :label="$t('Variant')" :value="selectedResponse.widgetVariant || `'${$t('Default')}'`"/>
                  </Stats>
                </CardSection>
              </Card>
              <!-- <Card>
                <CardSection>
                  <pre>{{JSON.parse(selectedResponse.json)}}</pre>
                </CardSection>
              </Card> -->
            </template>
          </Sheet>
        </LayoutSection>
      </Layout>

    </Page>

  </div>
</template>

<script>

import _ from 'lodash'

import Utils from '../classes/utils/Utils'

export default {
  name: 'AccountFormResponses',
  data () {
    return {
      loading: true,
      exporting: false,
      filter: {
        id: null,
        campaignId: [],
        widgetId: [],
        fromDate: null,
        toDate: null,
        status: null,
      },
      offset: 0,
      limit: 10,
      responses: [],
      totalElements: null,
      totalLoading: true,
      dataLoading: true,
      selectedResponse: null,
      checkedResponses: [],
      
      campaigns: [],
      widgets: [],
      sortBy: {
        column: 'createdOn',
        direction: 'desc'
      },
      cancelableResponses: null,
      cancelableTotals: null
    }
  },
  async mounted() {
    NProgress.start()

    await this.setFilterFromQuery(false)

    this.$watch('$route', async () => {
      await this.setFilterFromQuery(true)
    }, { deep: true })

    this.limit = this.getTableLimit('tableLimit') || 10
    this.cancelableResponses = Utils.makeCancelablePromise(
      this.api.getFormResponses(this.filter, this.limit, this.offset) 
    )
    this.cancelableResponses.promise
      .then((data) => {
        this.responses = data
        this.loading = false
      })
      .catch((e) => {
        if (!e.isCanceled) {
          this.apiCatch(e)
        }
      })
      .finally(() => {
        NProgress.done()
      })
    
    Promise.all([
      this.api.getCampaignNames(), 
      this.api.getWidgetNames()
    ])
      .then((data) => {
        const [ campaigns, widgets ] = data
        this.campaigns = campaigns
        this.widgets = widgets
        this.dataLoading = false
      })
      .catch(this.apiCatch)

    this.cancelableTotals = Utils.makeCancelablePromise(
      this.api.getFormResponsesTotal({
        fromDate:  this.filter.fromDate,
        status: this.filter.status
      })
    )
    this.cancelableTotals.promise
      .then((data) => {
        this.totalElements = data
        this.totalLoading = false

        // automatically show sheet if only one response
        if (this.responses && this.responses.length === 1) {
          this.selectedResponse = this.responses[0]
        }
      })
      .catch((e) => {
        if (!e.isCanceled) {
          this.apiCatch(e)
        }
      })
  },
  methods: {
    async setFilterFromQuery(makeApiCall = false) {
      if (this.$route.query) {
        this.filter = {}
        Object.keys(this.$route.query).forEach((k) => {
          if (this.$route.query[k]) {
            if (k === 'campaignId' || k === 'widgetId') {
              if (Array.isArray(this.$route.query[k])) {
                this.filter[k] = this.$route.query[k].map(strId => parseInt(strId))
              } else {
                this.filter[k] = [parseInt(this.$route.query[k])]
              }
            } else {
              this.filter[k] = this.$route.query[k]
            }
          } else {
            this.filter[k] = null
          }
        })
      }
      if (!this.filter.fromDate && !this.filter.id) {
        this.filter.fromDate = window.moment().startOf('month').format('YYYY-MM-DD')
      }

      await this.$router.replace({ name: 'AccountFormResponses', query: this.filter})
      .catch(this.duplicateRouteCatch)

      if (makeApiCall) {
        this.filterFormResponses(true)
      }
    },
    download() {
      NProgress.start()
      this.exporting = true
      this.api.getFormResponseDownloadUrl(this.filter)
        .then((response) => {
          if (response.file) {
            window.open(response.file)
          } else {
            this.eventHub.$emit('startBackgroundProcess')
          }
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.exporting = false
          NProgress.done()
        })
    },
    updateOffset(offset) {
      this.offset = offset
      this.filterFormResponses(false)
    },
    updateFilter(filter) {
      this.filter = filter
      this.filterFormResponses()
    },
    setStatus(status) {
      this.filter.status = status
      this.filterFormResponses()
    },
    updateLimit(limit) {
      this.setTableLimit('tableLimit', limit)
      this.limit = limit
      this.offset = 0
      this.filterFormResponses(false)
    },
    updateUrl() {
      this.$router.push({ name: 'AccountFormResponses', query: this.filter})
      .catch(this.duplicateRouteCatch)
    },
    checkResponse(response, event) {
      if (event.target.checked) {
        if (!this.checkedResponses.includes(response)) {
          this.checkedResponses.push(response)
        }
      } else {
        this.checkedResponses = this.checkedResponses.filter((r) => {
          return r.id !== response.id
        })
      }
    },
    archiveSelectedResponses() {
      for (const r of this.checkedResponses) {
        this.api.updateFormResponseStatus(r.id, 'ARCHIVED')
          .then(() => {
            const resp = this.responses.find(res => res.id === r.id)
            resp.status = 'ARCHIVED'
          })
          .catch(this.apiCatch)
      }
    },
    unarchiveSelectedResponses() {
      for (const r of this.checkedResponses) {
        this.api.updateFormResponseStatus(r.id, 'NEW')
          .then(() => {
            const resp = this.responses.find(res => res.id === r.id)
            resp.status = 'NEW'
          })
          .catch(this.apiCatch)
      }
    },
    filterFormResponses(refreshTotals = true) {
      this.updateUrl()
      NProgress.start()
      // const stat = this.filter.status !== 'All' ? this.filter.status : null  
      this.loading = true
      this.cancelableResponses.cancel()
      this.cancelableResponses = Utils.makeCancelablePromise(
        this.api.getFormResponses(this.filter, this.limit, this.offset)
      )
      this.cancelableResponses.promise
        .then((data) => {
          this.responses = data
          // automatically show sheet if only one response
          if (this.responses && this.responses.length === 1) {
            this.selectedResponse = this.responses[0]
          }
        })
        .catch((e) => {
          if (!e.isCanceled) {
            this.apiCatch(e)
          }
        })
        .finally(() => {
          this.loading = false
          NProgress.done()
        })

      if (refreshTotals) {
        this.totalElements = null
        this.totalLoading = true
        this.cancelableTotals.cancel()
        this.cancelableTotals = Utils.makeCancelablePromise(
          this.api.getFormResponsesTotal(this.filter)
        )
        this.cancelableTotals.promise
          .then((data) => {
            this.totalElements = data
            this.totalLoading = false
          })
          .catch((e) => {
            if (!e.isCanceled) {
              this.apiCatch(e)
            }
          })
      }
    },
    getCategories(obj) {
      if (!obj) {
        return ''
      }
      const cats = [];
      Object.keys(obj).forEach(k => {
        const value = obj[k];
        if (value) {
          cats.push(k + ' (' + value + ')')
        } else {
          cats.push(k)
        }
      })
      return cats.join(', ')
    }
  },
  computed: {
    loadingState() {
      return {
        rows: this.loading,
        totals: false,
        footer: this.totalLoading
      }
    },
    selectedProps() {
      const rv = []
      const model = this.selectedJson
      if (model) {
        Object.keys(model).forEach(k => {
          if (k !== 'cartDetails') {
            rv.push({
              name: k,
              value: model[k]
            })
          }
        })
      }
      return rv
    },
    selectedJson() {
      return this.selectedResponse && this.selectedResponse.json && JSON.parse(this.selectedResponse.json)
    },
    selectedCartJson() {
      return this.selectedResponse && this.selectedResponse.cartJSON && JSON.parse(this.selectedResponse.cartJSON)
    },
    selectedCart() {
      return this.selectedCartJson || (this.selectedJson && this.selectedJson.cartDetails)
    },
    cartTotal() {
      const cart = this.selectedCart;
      if (cart) {
        return cart.products.reduce(function (acc, product) {
          return acc + (product.price * (product.quantity || 1));
        }, 0);
      }
      return null
    },
    campaignList() {
      return this.campaigns.map(c => {
        return {
          label: c.name,
          value: c.id
        }
      })
    },
    widgetList() {
      return this.widgets.map(w => {
        return {
          label: w.name,
          value: w.id
        }
      })
    },
    sortedResponses() {
      let rows = this.responses
      if (this.sortBy) {
        rows = _.orderBy(rows, [this.sortBy.column], [this.sortBy.direction])
      }
      return rows
    }
  }
}

</script>
